// The price list shown on /pricing. Prices are per customer workspace (a tenant), in the customer's currency, WITHOUT GST.
//
// Where the numbers come from: napp-cloud-deploy/PRICING-COST-ANALYSIS.md works out what the production-standard AWS set-up for
// 10 tenants costs a month (3 x m6i.xlarge nodes, Multi-AZ PostgreSQL with 100 GB, the static-IP load balancers and NAT, ...), adds
// a buffer and a support allowance, and checks these prices against it. Change a price there first, then here.
//
// The plan limits are commercial terms: nothing in the platform counts messages or numbers against a plan yet.

export const ANNUAL_MONTHS_BILLED = 10; // a year paid in advance costs 10 months
export const GST_PERCENT = 18;          // charged to customers invoiced in rupees only
export const CONTACT_EMAIL = 'info@primedha.com';

// price.INR / price.USD are the MONTHLY prices; the yearly price is ANNUAL_MONTHS_BILLED x the monthly one (see priceFor).
// extraNumber is the monthly price of one more sender number, on request. null = not a fixed price.
export const PLANS = [
  {
    tier: 'trial',
    title: 'Trial',
    trial: true,
    price: null,
    limits: { wabas: 1, senders: 1, users: 2, messages: 100, mediaGb: 1 },
    features: { scheduling: true, buttonReplies: true, auditLog: true, onboarding: false },
    support: 'documentation',
    extraNumber: null,
  },
  {
    tier: 'starter',
    title: 'Starter',
    price: { INR: 9999, USD: 119 },
    limits: { wabas: 1, senders: 2, users: 5, messages: 10000, mediaGb: 5 },
    features: { scheduling: false, buttonReplies: false, auditLog: false, onboarding: false },
    support: 'email',
    extraNumber: { INR: 1499, USD: 19 },
  },
  {
    tier: 'growth',
    title: 'Growth',
    price: { INR: 29999, USD: 349 },
    limits: { wabas: 3, senders: 10, users: 15, messages: 50000, mediaGb: 25 },
    features: { scheduling: true, buttonReplies: true, auditLog: true, onboarding: false },
    support: 'priority',
    extraNumber: { INR: 1299, USD: 16 },
  },
  {
    tier: 'scale',
    title: 'Scale',
    price: { INR: 79999, USD: 899 },
    limits: { wabas: 10, senders: 40, users: 50, messages: 200000, mediaGb: 100 },
    features: { scheduling: true, buttonReplies: true, auditLog: true, onboarding: true },
    support: 'priority',
    extraNumber: { INR: 999, USD: 12 },
  },
  {
    tier: 'enterprise',
    title: 'Enterprise',
    enterprise: true,
    price: null,
    limits: { wabas: null, senders: null, users: null, messages: null, mediaGb: null },
    features: { scheduling: true, buttonReplies: true, auditLog: true, onboarding: true },
    support: 'dedicated',
    extraNumber: null,
  },
];

/** The price of a plan for one currency and billing interval ('monthly' | 'annual'), or null when the plan has no fixed price. */
export const priceFor = (plan, currency, interval) => {
  const monthly = plan.price && plan.price[currency];
  if (monthly == null) return null;
  return interval === 'annual' ? monthly * ANNUAL_MONTHS_BILLED : monthly;
};

/** 12,345 -> "₹12,345" (Indian digit grouping) or "$12,345". */
export const formatMoney = (currency, amount) => (
  currency === 'INR'
    ? `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
    : `$${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
);

const count = (n, singular, plural) => `${n.toLocaleString('en-IN')} ${n === 1 ? singular : plural}`;

const SUPPORT_LINE = {
  documentation: 'Documentation support',
  email: 'Email support (business hours)',
  priority: 'Priority support (business hours)',
  dedicated: 'Dedicated support (business hours)',
};

/** The bullet points of a plan, limits first and then what it includes. */
export const featureLines = (plan) => {
  const { limits: l, features: f } = plan;
  const lines = [
    l.wabas == null ? 'Unlimited WhatsApp Business Accounts' : count(l.wabas, 'WhatsApp Business Account', 'WhatsApp Business Accounts'),
    l.senders == null ? 'Unlimited sender numbers' : count(l.senders, 'sender number', 'sender numbers'),
    l.users == null ? 'Unlimited platform users' : count(l.users, 'platform user', 'platform users'),
    l.messages == null ? 'Message volume agreed per contract' : `${l.messages.toLocaleString('en-IN')} messages ${plan.trial ? 'in all' : 'a month'}`,
    l.mediaGb == null ? 'Media storage agreed per contract' : `${l.mediaGb} GB media storage`,
    'Templates, categories and variables',
    'Opt-in and opt-out consent',
    'REST API for your own systems',
    'Personal data encrypted with a dedicated key',
  ];
  if (f.scheduling) lines.push('Scheduled sending');
  if (f.buttonReplies) lines.push('Button replies and customer interest');
  if (f.auditLog) lines.push('Compliance audit log of personal-data access');
  if (f.onboarding) lines.push('Guided onboarding');
  lines.push(SUPPORT_LINE[plan.support]);
  return lines;
};
